import React, { useState, useCallback } from 'react';
import { monsterClasses } from '../monsters';
import { getWs } from '../connection';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import AlertOverlay from './game/overlays/AlertOverlay';
import { selectMyPlayer, selectPlayers, savePlayerName, selectRoomId, leaveRoom, startGame, pickMonster, selectIsTestEnv, kickPlayer } from '../redux/gameSlice';
import { showAlert } from '../redux/overlaySlice';
import { Trans, t } from '@lingui/macro';
import LanguageSelector from './LanguageSelector';
import i18n from '../i18n';
import { monsterClassFromId } from '../monsters';

function closeRoom(roomId) {
  const ws = getWs(roomId);
  ws.gsend({ action: 'close-room' });
}

function Lobby(props) {
  const dispatch = useDispatch();
  const roomId = useSelector(selectRoomId);
  const gamePlayers = useSelector(selectPlayers);
  const myPlayer = useSelector(selectMyPlayer);
  const isTestEnv = useSelector(selectIsTestEnv);
  const needsNameUpdate = myPlayer.name === null;
  const [newPlayerName, setNewPlayerName] = useState(() => myPlayer.name ?? '');
  const [copiedTooltip, setCopiedTooltip] = useState(false);
  const leaveRoomCb = useCallback(() => {
    dispatch(leaveRoom());
    props.history.push("/");
  }, [dispatch, props.history]);

  const startGameCb = useCallback(() => {
    if (gamePlayers.length < 2) {
      dispatch(showAlert({
        canEsc: true,
        alertType: "error",
        alertTitle: i18n._(t`You can't play alone!`),
        alertText: i18n._(t`This is a game meant to be played by 2 to 6 players, and best enjoyed while talking with your friends`)
      }));
      return;
    }

    for (let player of gamePlayers) {
      if (!player.name || player.monster_id === -1) {
        dispatch(showAlert({
          canEsc: false,
          alertType: "confirmation",
          alertTitle: i18n._(t`Not everyone has picked their name & monster!`),
          alertText: i18n._(t`If you start the game now, we will pick a random name or monster for the players who didn't choose yet`),
          alertButtons: [
            {
              content: i18n._(t`START NOW`),
              action: startGame()
            },
            {
              content: i18n._(t`WAIT`)
            },
          ],
        }));
        return;
      }
    }

    if (gamePlayers.length < 6 && !isTestEnv) {
      dispatch(showAlert({
        canEsc: true,
        alertType: "info",
        alertTitle: i18n._(t`Is everyone ready?`),
        alertText: i18n._(t`Once you start the game, no new people can join this room until you finish (or create a new room). If everyone's here already, go ahead and start!`),
        alertButtons: [
          {
            content: i18n._(t`WAIT LONGER`)
          },
          {
            content: i18n._(t`START`),
            action: startGame()
          },
        ],
      }));
      return;
    }

    dispatch(startGame(roomId));
  }, [dispatch, roomId, gamePlayers, isTestEnv]);

  const savePlayerNameCb = useCallback((ev) => {
    ev.preventDefault();
    dispatch(savePlayerName(newPlayerName));
  }, [dispatch, newPlayerName]);

  const needToPickMonster = myPlayer.monster_id === -1;
  let lobbyMonsters = [];
  if (needToPickMonster) {
    for (let monsterId = 0; monsterId < monsterClasses.length; ++monsterId) {
      const monsterClass = monsterClassFromId(monsterId);
      const playerWithMonster = gamePlayers.find((player) => player.monster_id === monsterId);
      if (!playerWithMonster) {
        const onClick = needToPickMonster ? () => dispatch(pickMonster(monsterId)) : null;
        lobbyMonsters.push(<div className="lobby-player monster-available" key={monsterId} onClick={onClick}>
          <div className="lobby-player-name"></div>
          <div className={`lobby-player-image ${monsterClass}`}></div>
        </div>);
      } else {
        const extraClassGameOwner = playerWithMonster.is_owner ? 'game-owner' : '';
        const extraClassMyPlayerName = playerWithMonster.id === myPlayer.id ? 'my-lobby-player-name' : '';

        lobbyMonsters.push(<div className={`lobby-player monster-taken ${extraClassGameOwner}`} key={monsterId}>
          <div className={`lobby-player-name ${extraClassMyPlayerName}`}>{playerWithMonster.name ?? '...'}</div>
          <div className={`lobby-player-image ${monsterClass}`}></div>
          <div className="game-owner-container"><Trans>GAME OWNER</Trans></div>
        </div>);
      }
    }
  } else {
    for (let player of gamePlayers) {
      const extraClassGameOwner = player.is_owner ? 'game-owner' : '';
      const extraClassMyPlayerName = player.id === myPlayer.id ? 'my-lobby-player-name' : '';
      const monsterClass = monsterClassFromId(player.monster_id);

      lobbyMonsters.push(<div className={`lobby-player monster-taken ${extraClassGameOwner}`} key={player.id}>
        <div className={`lobby-player-name ${extraClassMyPlayerName}`}>{player.name ?? '...'}</div>
        <div className={`lobby-player-image ${monsterClass}`}></div>
        <div className="game-owner-container"><Trans>GAME OWNER</Trans></div>
        {myPlayer.is_owner && player !== myPlayer ? <button className="kick-player-button" onClick={() => dispatch(kickPlayer(player.id))}><Trans>KICK</Trans></button> : null}
      </div>);
    }

    for (let i = 0; i < 6 - gamePlayers.length; ++i) {
      lobbyMonsters.push(<div className="lobby-player no-player" key={`placeholder-${i}`}>
        <div className="lobby-player-name"></div>
        <div className="lobby-player-image unknown-monster no-player"></div>
      </div>);
    }
  }

  let actionButtons = "";
  if (myPlayer.is_owner) {
    actionButtons = [<button key="close-btn" className="button" onClick={() => closeRoom(roomId)}><Trans>CLOSE ROOM</Trans></button>];
    if (needToPickMonster === false) {
      actionButtons.push(<button key="start-btn" className="button" onClick={startGameCb}><Trans>START THE GAME</Trans></button>);
    }
  } else {
    actionButtons = <button className="button" onClick={leaveRoomCb}><Trans>LEAVE ROOM</Trans></button>;
  }

  const copyToClipboard = () => {
    const el = document.createElement('textarea');
    el.value = window.location.href;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);

    setCopiedTooltip(true);
    setTimeout(() => setCopiedTooltip(false), 2000);
  };

  return <div id="content">
    <AlertOverlay />

    <div id="player-name-choice-overlay" className={`overlay-background dark-background-overlay ${needsNameUpdate === false ? 'hidden' : ''}`}>
      <div className="overlay-wrapper">
        <div id="player-name-choice-image" className="rey-regular"></div>
        <div id="circle-behind-player-name-choice-image"></div>
        <div id="player-name-choice-input-container">
          <div className="overlay-title"><Trans id="lobby.player_name_choice.title">Player name choice</Trans></div>
          <div className="overlay-text-explanation"><Trans id="lobby.player_name_choice.desc">Type in your player name below before you can join this game room</Trans></div>
          <form id="input-container" onSubmit={savePlayerNameCb}>
            <input name="player-name" id="player-name" placeholder={i18n._(t`Your player name`)} autoFocus autoComplete="off" maxLength="16" onChange={(ev) => setNewPlayerName(ev.target.value.replace(/[<>[\]()\\/&%$·!"=?¿ª*-.,+`´;]/g, ""))} />
            <button type="submit" className="button" id="send-player-name" disabled={newPlayerName.length <= 1 || newPlayerName.length > 16}>
              <span className="button-content-fixed-width"><i className="fas fa-caret-right"></i></span>
            </button>
          </form>
        </div>
      </div>
    </div>
    <div id="lobby" className="noselect">
      <div id="lobby-header">
        <div id="game-room-ID-container" onClick={copyToClipboard}>
          <span className="tooltip-wrap" aligned="right">
            <span className="tooltip">
              {copiedTooltip === false ? <Trans>Click to copy the invitation link</Trans> : <Trans>Copied!</Trans>}
            </span>
          </span>
          <p className="game-room-ID-title"><Trans>GAME ROOM</Trans></p>
          <p className="game-room-ID">{roomId}</p>
        </div>
        <LanguageSelector />
      </div>
      <p className="welcome-to-lobby">
        {needToPickMonster === false ? <Trans>Waiting for other players to join...</Trans> : null}
      </p>
      <p className={`pick-monster-message ${needToPickMonster === false ? 'message-invisible' : ''}`}>
        <Trans>Pick the monster you want to play with</Trans>
      </p>
      <div id="lobby-players-container" className={needToPickMonster ? 'picking-monster' : 'monster-picked'}>
        {lobbyMonsters}
      </div>
      <div id="action-buttons" className="noselect">
        {actionButtons}
      </div>
      <div id="lobby-logo-game-container">
        <div className="lobby-logo-game"></div>
      </div>
    </div>
  </div>;
}

export default withRouter(Lobby);
